import * as React from 'react';
import {
  FormProvider,
  useController,
  useForm,
  type ControllerRenderProps,
  type FieldValues,
  type Path,
  type Resolver,
  type UseFormProps,
  type UseFormReturn,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert } from './Alert';
import { Field, type FieldProps } from './Field';
import { Stack, type StackProps } from './Layout';

/**
 * A form is a zod schema, and everything else follows from it.
 *
 *   const schema = z.object({
 *     name: z.string().min(1, 'Name is required'),
 *     phone: z.string().optional(),
 *   });
 *
 *   const form = useZodForm(schema, { defaultValues: { name: '', phone: '' } });
 *
 *   <Form form={form} onSubmit={create}>
 *     <FormField form={form} name="name" label="Name">
 *       {(field) => <Input {...field} />}
 *     </FormField>
 *     <FormField form={form} name="phone" label="Phone">
 *       {(field) => <PhoneInput {...field} />}
 *     </FormField>
 *     <FormActions>…</FormActions>
 *   </Form>
 *
 * The schema decides three things at once, so they cannot disagree: the type
 * of `values` handed to `onSubmit`, which fields get the required marker
 * (anything the schema will not accept `undefined` for), and the message that
 * shows under a field when it fails. `FormField` takes the form rather than
 * finding it on context so that `name` is typed against the schema: a
 * misspelt field is a compile error rather than a silently unbound input.
 *
 * Before this, the same product had four ways of validating a form: hand-set
 * `Field required` beside browser bubbles, react-hook-form rules with the
 * marker set separately, a `toast.error('X is required')` on submit, and a
 * watch()-driven `isFormValid` that only disabled the button. The browser's
 * bubble was the default because `Field required` puts the native attribute
 * on the control and nothing set `noValidate`. This form does, so the message
 * always appears under the field.
 */

/** Any zod object schema, whatever its shape. */
export type FormSchema = z.ZodType<FieldValues, z.ZodTypeDef, FieldValues>;

/**
 * A react-hook-form instance that remembers the schema it validates with.
 * `Form` reads the schema back to decide which fields are required.
 */
export type ZodForm<S extends FormSchema> = UseFormReturn<z.infer<S>> & {
  schema: S;
  /**
   * The names a `FormField` is currently drawing. `handleSubmit` reads it to
   * tell a message that has somewhere to go from one that does not.
   */
  shownFields: Set<string>;
};

export type UseZodFormOptions<S extends FormSchema> = Omit<
  UseFormProps<z.infer<S>>,
  'resolver'
>;

/**
 * The refinements wrapped around a schema, outermost first. `.refine()` and
 * `.superRefine()` both land here as `ZodEffects`.
 */
function refinementsOf(schema: z.ZodTypeAny) {
  const refinements: ((value: never, ctx: z.RefinementCtx) => unknown)[] = [];
  let current: z.ZodTypeAny = schema;
  while (current instanceof z.ZodEffects) {
    const effect = current._def.effect;
    if (effect.type === 'refinement') refinements.push(effect.refinement);
    current = current._def.schema;
  }
  return refinements;
}

/** Put an issue's message at its path in RHF's nested error object. */
function placeIssue(
  errors: Record<string, unknown>,
  path: (string | number)[],
  message: string
) {
  if (!path.length) return;
  let node = errors;
  for (const key of path.slice(0, -1)) {
    const next = node[String(key)];
    if (!next || typeof next !== 'object') node[String(key)] = {};
    node = node[String(key)] as Record<string, unknown>;
  }
  const last = String(path[path.length - 1]);
  // A message already there was the schema's own and wins.
  if (
    node[last] &&
    typeof node[last] === 'object' &&
    'message' in (node[last] as object)
  )
    return;
  node[last] = { type: 'custom', message };
}

/**
 * `zodResolver`, with the schema's conditional rules answered even when
 * another field is wrong.
 *
 * zod runs a `superRefine` only once the object it wraps has parsed, so a
 * form where the title is missing said nothing about the year, make and
 * model a vehicle also needs — they lit up one submit later, which reads as
 * a form inventing new objections. Those refinements are run here against
 * the values as they stand, and their messages merged in under any the
 * schema itself produced.
 */
function withConditionalRules<S extends FormSchema>(
  schema: S
): Resolver<z.infer<S>> {
  const base = zodResolver(schema);
  const refinements = refinementsOf(schema);
  if (!refinements.length) return base as Resolver<z.infer<S>>;

  return async (values, context, options) => {
    const result = await (base as Resolver<z.infer<S>>)(
      values,
      context,
      options
    );
    const errors = result.errors as Record<string, unknown> | undefined;
    // Nothing failed, so zod already ran them.
    if (!errors || !Object.keys(errors).length) return result;

    for (const refinement of refinements) {
      try {
        refinement(
          values as never,
          {
            path: [],
            addIssue: (issue: z.IssueData) => {
              const message =
                'message' in issue && issue.message
                  ? issue.message
                  : 'This is not valid.';
              placeIssue(errors, issue.path ?? [], message);
            },
          } as unknown as z.RefinementCtx
        );
      } catch {
        /* A refinement written against parsed values can throw on raw ones.
           Its own fields keep whatever the schema said about them. */
      }
    }
    return result;
  };
}

/**
 * `useForm` wired to a zod schema.
 *
 * Validates on submit, then on every change of a field that has failed —
 * the message appears once the user has tried, and clears as they fix it.
 */
export function useZodForm<S extends FormSchema>(
  schema: S,
  options: UseZodFormOptions<S> = {}
): ZodForm<S> {
  const form = useForm<z.infer<S>>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    ...options,
    resolver: withConditionalRules(schema),
  });
  const shownFields = React.useRef(new Set<string>()).current;
  /*
   * RHF's own handleSubmit, captured before the wrapper below is assigned
   * over it — `form` is the same object every render, so reading it again
   * later would hand the wrapper itself and recurse.
   */
  const submitThrough = React.useRef(form.handleSubmit).current;
  /**
   * A form never refuses in silence. If the schema rejects a field no
   * `FormField` is drawing — one the form does not show, or forgot to bind —
   * the message would otherwise go nowhere and the button would look broken.
   * Those are gathered to the top of the form instead.
   *
   * This wraps `handleSubmit` rather than living in `Form`, because a form
   * with two submit paths calls `handleSubmit` itself from each button.
   */
  const handleSubmit: typeof form.handleSubmit = (onValid, onInvalid) =>
    submitThrough(
      (values, event) => onValid(values, event),
      (errors, event) => {
        const orphaned = errorPaths(errors).filter(
          (path) => !shownFields.has(path)
        );
        if (orphaned.length) {
          const messages = orphaned
            .map((path) => messageAt(errors, path))
            .filter(Boolean);
          form.setError('root.server', {
            type: 'unshown',
            message: messages.length
              ? messages.join(' ')
              : 'This form could not be submitted.',
          });
        }
        return onInvalid?.(errors, event);
      }
    );

  // useForm hands back the same object every render, so this is a stable
  // reference too.
  return Object.assign(form, { schema, shownFields, handleSubmit });
}

/**
 * Whether the schema rejects an empty value at this path. That is what the
 * required marker means: the form cannot be submitted with nothing here.
 *
 * "Nothing" is `undefined` or the empty string, because a text input's
 * cleared state is `''` — `z.string()` alone accepts it and so is not
 * required, where `z.string().min(1)` rejects it and is. Enums and numbers
 * reject both, and anything `.optional()` or `.default()` accepts `undefined`.
 *
 * Walks dotted paths ("drivers.0.name") through objects and arrays, and
 * through the wrappers zod puts around a type — `.optional()`, `.nullable()`,
 * `.default()`, `.refine()` — so a refined string still reports correctly.
 */
export function isRequiredPath(schema: z.ZodTypeAny, path: string): boolean {
  let current: z.ZodTypeAny | undefined = schema;
  for (const segment of path.split('.')) {
    current = unwrap(current);
    if (!current) return false;
    if (current instanceof z.ZodObject) {
      current = current.shape[segment];
    } else if (current instanceof z.ZodArray) {
      current = current.element;
    } else {
      return false;
    }
  }
  if (!current) return false;
  const field = current;
  return [undefined, ''].every((empty) => !field.safeParse(empty).success);
}

/** Strip the wrappers that carry no shape of their own. */
function unwrap(type: z.ZodTypeAny | undefined): z.ZodTypeAny | undefined {
  let current = type;
  for (;;) {
    if (current instanceof z.ZodEffects) current = current.innerType();
    else if (
      current instanceof z.ZodOptional ||
      current instanceof z.ZodNullable
    )
      current = current.unwrap();
    else if (current instanceof z.ZodDefault) current = current.removeDefault();
    else return current;
  }
}

export interface FormProps<S extends FormSchema>
  extends Omit<StackProps, 'as' | 'onSubmit'> {
  form: ZodForm<S>;
  /** Receives values the schema has already accepted. */
  onSubmit: (values: z.infer<S>) => void | Promise<void>;
  /**
   * Where the form-wide error goes — the message `applyServerErrors` leaves
   * when the server refuses the whole submission rather than one field.
   * `top` is the default: it sits under the dialog's title, where the eye is
   * when the request fails. `bottom` puts it above the actions instead.
   */
  errorPlacement?: 'top' | 'bottom';
}

/** Every path an error object carries, flattened to "a" / "a.0.b". */
function errorPaths(errors: unknown, prefix = ''): string[] {
  if (!errors || typeof errors !== 'object') return [];
  const found: string[] = [];
  for (const [key, value] of Object.entries(
    errors as Record<string, unknown>
  )) {
    if (key === 'root' || key === 'ref' || key === 'types') continue;
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && 'message' in value) {
      found.push(path);
    } else {
      found.push(...errorPaths(value, path));
    }
  }
  return found;
}

/** The message at a flattened path. */
function messageAt(errors: unknown, path: string): string | undefined {
  const at = path
    .split('.')
    .reduce<unknown>(
      (acc, key) =>
        acc && typeof acc === 'object'
          ? (acc as Record<string, unknown>)[key]
          : undefined,
      errors
    );
  return at && typeof at === 'object' && 'message' in at
    ? String((at as { message?: unknown }).message ?? '')
    : undefined;
}

/**
 * The `<form>` element: spacing from Stack, submission through the schema,
 * and `noValidate` so the browser leaves the messages to us.
 *
 * A form never refuses in silence. If the schema rejects a field that no
 * `FormField` is drawing — one the form does not show, or forgot to bind —
 * the message would otherwise go nowhere and the submit button would look
 * broken. Those messages are gathered to the top of the form instead. Seeing
 * one means a field is missing its binding, or the schema is asking for
 * something the form never collects.
 */
export function Form<S extends FormSchema>({
  form,
  onSubmit,
  errorPlacement = 'top',
  gap = 6,
  children,
  ...props
}: FormProps<S>) {
  const rootError = form.formState.errors.root?.server?.message;
  // Stack is typed as a div whatever `as` says, and a div has no
  // `noValidate`; the attribute reaches the <form> all the same.
  const formOnly = { noValidate: true } as React.HTMLAttributes<HTMLDivElement>;
  const error = rootError ? (
    <Alert tone="destructive" role="alert">
      {rootError}
    </Alert>
  ) : null;

  return (
    // The provider is for a nested component that wants `useFormContext`;
    // FormField itself is handed the form.
    <FormProvider {...form}>
      <Stack
        as="form"
        gap={gap}
        {...formOnly}
        // Only the values: handleSubmit would also pass the event, and a
        // handler that is a mutation's `mutate` must not receive it.
        onSubmit={form.handleSubmit((values) => onSubmit(values))}
        {...props}
      >
        {errorPlacement === 'top' ? error : null}
        {children}
        {errorPlacement === 'bottom' ? error : null}
      </Stack>
    </FormProvider>
  );
}

/** What a field knows about itself, for a control that draws its own label. */
export interface FormFieldState {
  /** The message `FormField` is already showing under the control. */
  error?: string;
  invalid: boolean;
  /** From the schema, unless the `required` prop overrides it. */
  required: boolean;
}

export type FormFieldControl<
  S extends FormSchema,
  N extends Path<z.infer<S>>,
> = ControllerRenderProps<z.infer<S>, N>;

export interface FormFieldProps<
  S extends FormSchema,
  N extends Path<z.infer<S>>,
> extends Omit<FieldProps, 'error' | 'required' | 'children'> {
  form: ZodForm<S>;
  name: N;
  /**
   * The control. A function receives the bound props — `value`, `onChange`,
   * `onBlur`, `name`, `ref` — to spread onto any input, native or not:
   *
   *   {(field) => <Input {...field} />}
   *   {(field) => <Select value={field.value} onValueChange={field.onChange} />}
   *
   * A second argument carries what the field knows about itself, for a
   * control that draws its own label or its own invalid tone — EntityPicker,
   * say, where the label lives inside the control:
   *
   *   {(field, state) => <EntityPicker required={state.required} error={state.invalid} … />}
   *
   * Plain children render as given, for the rare control bound by other means.
   */
  children:
    | React.ReactNode
    | ((
        field: FormFieldControl<S, N>,
        state: FormFieldState
      ) => React.ReactNode);
  /**
   * Override the marker the schema implies. For a field the schema requires
   * but the UI fills in itself, or one it does not require but the copy
   * should still flag.
   */
  required?: boolean;
}

/**
 * A `Field` bound to one path of a `Form`: the label, the required marker
 * from the schema, and the error from the form's state.
 */
export function FormField<S extends FormSchema, N extends Path<z.infer<S>>>({
  form,
  name,
  children,
  required,
  ...props
}: FormFieldProps<S, N>) {
  const { field, fieldState } = useController<z.infer<S>, N>({
    name,
    control: form.control,
  });

  // Tell the form this name has somewhere to show its message.
  const { shownFields } = form;
  React.useEffect(() => {
    shownFields.add(name);
    return () => {
      shownFields.delete(name);
    };
  }, [shownFields, name]);

  const state: FormFieldState = {
    error: fieldState.error?.message,
    invalid: !!fieldState.error,
    required: required ?? isRequiredPath(form.schema, name),
  };

  return (
    <Field error={state.error} required={state.required} {...props}>
      {typeof children === 'function' ? children(field, state) : children}
    </Field>
  );
}

/**
 * What the backend sends for a rejected request. Nest's ValidationPipe puts
 * one message per failed rule in `message`, each starting with the property
 * name ("email must be an email"); other handlers send a single string.
 */
interface ServerErrorBody {
  message?: string | string[];
  error?: string;
}

function readBody(error: unknown): ServerErrorBody | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const response = (error as { response?: { data?: unknown } }).response;
  const data = response?.data;
  return data && typeof data === 'object'
    ? (data as ServerErrorBody)
    : undefined;
}

/**
 * Put a failed submission's errors where they belong.
 *
 * A validation message that names a field the form has goes under that field;
 * anything else becomes the form-wide error that `Form` renders. `message`
 * replaces that fallback for a handler that already knows how to read the
 * error — the driver dialog's "that email is already in use", for instance.
 *
 * Root errors are cleared by react-hook-form on the next submit, so a
 * corrected resubmission starts clean.
 */
export function applyServerErrors<T extends FieldValues>(
  form: UseFormReturn<T>,
  error: unknown,
  message?: string
): void {
  const body = readBody(error);
  const values = form.getValues();
  const messages = Array.isArray(body?.message) ? body.message : [];

  let placed = false;
  for (const text of messages) {
    const [property] = text.split(' ', 1);
    if (property && property in values) {
      form.setError(
        property as Path<T>,
        {
          type: 'server',
          message: text.charAt(0).toUpperCase() + text.slice(1),
        },
        { shouldFocus: !placed }
      );
      placed = true;
    }
  }
  if (placed) return;

  const fallback =
    message ??
    (typeof body?.message === 'string' ? body.message : undefined) ??
    (messages.length ? messages.join('. ') : undefined) ??
    (error instanceof Error ? error.message : undefined) ??
    'Something went wrong. Please try again.';
  form.setError('root.server', { type: 'server', message: fallback });
}
